/**
 * API Service - Handles all HTTP requests to backend
 */

const API_URL = process.env.REACT_APP_API_URL || '';

// Module-level cache for auth token (avoids localStorage reads on every API call)
let cachedToken = null;

// Module-level cache for in-flight requests (prevents duplicate API calls)
const inflightRequests = new Map();

/**
 * Get auth token from localStorage (with in-memory cache)
 */
function getToken() {
  if (cachedToken !== null) {
    return cachedToken;
  }
  cachedToken = localStorage.getItem('authToken');
  return cachedToken;
}

/**
 * Set auth token in localStorage and cache
 */
function setToken(token) {
  cachedToken = token; // Update cache immediately
  localStorage.setItem('authToken', token);
}

/**
 * Remove auth token from localStorage and clear cache
 */
function removeToken() {
  cachedToken = null; // Clear cache
  localStorage.removeItem('authToken');
}

/**
 * Make authenticated API request with deduplication
 */
async function fetchAPI(endpoint, options = {}) {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Create request key for deduplication (method + endpoint + body)
  const method = options.method || 'GET';
  const bodyKey = options.body ? JSON.stringify(options.body) : '';
  const requestKey = `${method}:${endpoint}:${bodyKey}`;

  // Check if identical request is already in flight
  if (inflightRequests.has(requestKey)) {
    // console.log('[fetchAPI] Deduplicating request:', requestKey);
    return inflightRequests.get(requestKey);
  }

  // Create request promise
  const requestPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('[fetchAPI] Error response:', error);

        // Only reload on 401 if we have a token (expired/invalid token)
        // Don't reload on login failures (wrong credentials)
        if (response.status === 401 && token && endpoint !== '/api/auth/login') {
          // console.log('[fetchAPI] 401 with valid token - removing and reloading');
          removeToken();
          window.location.reload();
        }

        throw new Error(error.error || 'Something happened! Try again later.');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[fetchAPI] Request failed:', {
        url: endpoint,
        error: error.message,
        type: error.constructor.name
      });
      // Handle network errors (server down, no connection, etc.)
      if (error instanceof TypeError || error.message?.includes('fetch')) {
        throw new Error('Server is unavailable. Please try again later.');
      }
      // Re-throw other errors (already Error objects with message)
      throw error;
    } finally {
      // Clean up inflight request after completion (success or error)
      inflightRequests.delete(requestKey);
    }
  })();

  // Store in-flight request
  inflightRequests.set(requestKey, requestPromise);

  return requestPromise;
}

/**
 * Auth API
 */
export const authAPI = {
  async login(email, password) {
    // console.log('[API] ===== SENDING LOGIN REQUEST =====');
    // console.log('[API] Email:', email);
    // console.log('[API] Endpoint:', `${API_URL}/api/auth/login`);

    try {
      const data = await fetchAPI('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      // console.log('[API] ✅ Login response received');
      // console.log('[API] Response keys:', Object.keys(data));
      // console.log('[API] Response:', data);

      // Development mode: direct token response
      if (data.token) {
        // console.log('[API] Dev mode detected - storing token');
        // console.log('[API] Token preview:', data.token.substring(0, 20) + '...');
        setToken(data.token);
        return data;
      }

      // Production mode: OAuth redirect
      if (data.ssoUrl) {
        // console.log('[API] 🔄 Production mode - OAuth redirect');
        // console.log('[API] Redirect URL:', data.ssoUrl);
        // console.log('[API] Redirecting to Thinkific...');
        window.location.href = data.ssoUrl;
        return data;
      }

      console.warn('[API] ⚠️ Unexpected response - no token or ssoUrl');
      return data;
    } catch (error) {
      console.error('[API] ❌ Login request failed:', error.message);
      throw error;
    }
  },

  async getCurrentUser() {
    return fetchAPI('/api/auth/me');
  },

  async updateProfile(profileData) {
    // console.log('[API] Updating user profile:', profileData);
    return fetchAPI('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  },

  async logout() {
    try {
      const data = await fetchAPI('/api/auth/logout', {
        method: 'POST'
      });

      // Clear local token
      removeToken();

      // Clear Thinkific session cookies using an iframe
      // This prevents authentication loops when logging back in
      if (data.thinkificLogoutUrl) {
        // console.log('[API] Clearing Thinkific session via iframe');

        // Create hidden iframe to trigger Thinkific logout
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = data.thinkificLogoutUrl;
        document.body.appendChild(iframe);

        // Wait a moment for logout to complete, then redirect to app
        setTimeout(() => {
          document.body.removeChild(iframe);
          window.location.href = data.appUrl || '/';
        }, 1000);
      } else {
        // Fallback: just reload the page
        window.location.href = '/';
      }

      return data;
    } catch (error) {
      // Even if logout fails on server, clear local token
      removeToken();
      window.location.href = '/';
      throw error;
    }
  }
};

/**
 * Chat API
 */
export const chatAPI = {
  async getHistory() {
    // console.log('[API] Getting chat history');
    return fetchAPI('/api/chat/history');
  },

  async sendMessage(message, action = null) {
    // console.log('[API] Sending chat message:', message, 'action:', action);
    const data = await fetchAPI('/api/chat/message', {
      method: 'POST',
      body: JSON.stringify({ message, action })
    });
    // console.log('[API] Chat message response:', data);
    return data;
  },

  async streamMessage(message, action = null, onChunk) {
    // console.log('[API] Streaming chat message:', message, 'action:', action);
    const token = getToken();

    const response = await fetch(`${API_URL}/api/chat/message/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ message, action })
    });

    if (!response.ok) {
      throw new Error('Failed to stream message');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let conversationId = null;
    let streamingMessage = { content: '', timestamp: new Date().toISOString() };
    let planMessages = []; // Store plan messages that arrive after main response

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'start') {
              conversationId = data.conversationId;
            } else if (data.type === 'chunk') {
              streamingMessage.content += data.content;
              onChunk(streamingMessage.content);
            } else if (data.type === 'done') {
              // Store the main response but don't return yet
              // (might have plan_message or tool_response events coming)
              streamingMessage.userMessage = data.userMessage;
              streamingMessage.assistantMessage = data.assistantMessage;
            } else if (data.type === 'plan_message') {
              // Capture plan messages sent after tool execution
              // console.log('[API] Received plan message:', data.message);
              planMessages.push(data.message);
            } else if (data.type === 'tool_response') {
              // Capture tool response (Claude's final response after seeing tool results)
              console.log('[API] Received tool response:', data.message);
              console.log('[API] Tool response content length:', data.message.content?.length);
              console.log('[API] Tool response preview:', data.message.content?.substring(0, 200));

              // Add the tool response as a separate message
              if (!streamingMessage.toolResponses) {
                streamingMessage.toolResponses = [];
              }
              streamingMessage.toolResponses.push(data.message);

              // CRITICAL: Update streamingMessage.content to include the tool response
              // This ensures the content is persisted, not just displayed temporarily
              streamingMessage.content = streamingMessage.content + '\n' + data.message.content;

              // Also update the display in real-time
              onChunk(streamingMessage.content);
            } else if (data.type === 'widget_message') {
              // Capture widget messages sent after tool execution
              // console.log('[API] Received widget message:', data.message);
              if (!streamingMessage.widgetMessages) {
                streamingMessage.widgetMessages = [];
              }
              streamingMessage.widgetMessages.push(data.message);
            } else if (data.type === 'todos_updated') {
              // Todos were modified (added/edited/removed)
              console.log('[API] Todos updated - setting flag for refetch');
              streamingMessage.todosUpdated = true;
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (e) {
            console.error('[API] Error parsing SSE data:', e);
            // Skip invalid JSON
          }
        }
      }
    }

    // Return all messages including plan messages, tool responses, and widget messages
    return {
      conversationId,
      userMessage: streamingMessage.userMessage,
      assistantMessage: streamingMessage.assistantMessage,
      planMessages: planMessages,
      toolResponses: streamingMessage.toolResponses || [],
      widgetMessages: streamingMessage.widgetMessages || [],
      todosUpdated: streamingMessage.todosUpdated || false
    };
  },

  async clearConversation() {
    // console.log('[API] Clearing conversation');
    return fetchAPI('/api/chat/clear', {
      method: 'POST'
    });
  }
};

/**
 * Todos API
 */
export const todosAPI = {
  async getTodos(conversationId) {
    // console.log('[API] Getting todos for conversation:', conversationId);
    return fetchAPI(`/api/todos?conversationId=${conversationId}`);
  },

  async toggleTodo(todoId) {
    // console.log('[API] Toggling todo:', todoId);
    return fetchAPI(`/api/todos/${todoId}/toggle`, {
      method: 'PUT'
    });
  },

  async getProgressSummary(conversationId) {
    // console.log('[API] Getting progress summary for conversation:', conversationId);
    return fetchAPI(`/api/todos/progress?conversationId=${conversationId}`);
  },

  async updateTodo(todoId, todoText) {
    // console.log('[API] Updating todo:', todoId, 'New text:', todoText);
    return fetchAPI(`/api/todos/${todoId}/update`, {
      method: 'PUT',
      body: JSON.stringify({ todo_text: todoText })
    });
  },

  async deleteTodo(todoId) {
    // console.log('[API] Deleting todo:', todoId);
    return fetchAPI(`/api/todos/${todoId}`, {
      method: 'DELETE'
    });
  },

  async updateStage(conversationId, tierNumber, tierTitle) {
    // console.log('[API] Updating stage:', conversationId, tierNumber, 'New title:', tierTitle);
    return fetchAPI(`/api/todos/stage/${conversationId}/${tierNumber}`, {
      method: 'PUT',
      body: JSON.stringify({ tier_title: tierTitle })
    });
  },

  async deleteStage(conversationId, tierNumber) {
    // console.log('[API] Deleting stage:', conversationId, tierNumber);
    return fetchAPI(`/api/todos/stage/${conversationId}/${tierNumber}`, {
      method: 'DELETE'
    });
  },

  async approvePlan(conversationId, plan, objective, wasCompleted) {
    // console.log('[API] Approving plan for conversation:', conversationId);
    return fetchAPI('/api/todos/approve', {
      method: 'POST',
      body: JSON.stringify({
        conversationId,
        plan,
        objective,
        wasCompleted
      })
    });
  },

  async proposePlan(missionText, templateId = null, conversationContext = null, existingPlan = null) {
    console.log('[API] Proposing plan for mission:', missionText);
    console.log('[API] Has existing plan:', !!existingPlan);
    return fetchAPI('/api/todos/propose', {
      method: 'POST',
      body: JSON.stringify({
        missionText,
        templateId,
        conversationContext,
        existingPlan
      })
    });
  }
};

/**
 * Content API (Articles, News, Videos)
 */
export const contentAPI = {
  async getArticles(search = '') {
    const endpoint = search ? `/api/content/articles?search=${encodeURIComponent(search)}` : '/api/content/articles';
    return fetchAPI(endpoint);
  },

  async getArticle(slug) {
    return fetchAPI(`/api/content/articles/${slug}`);
  },

  async getNews(search = '') {
    const endpoint = search ? `/api/content/news?search=${encodeURIComponent(search)}` : '/api/content/news';
    return fetchAPI(endpoint);
  },

  async getNewsItem(id) {
    return fetchAPI(`/api/content/news/${id}`);
  },

  async getVideos(search = '') {
    const endpoint = search ? `/api/content/videos?search=${encodeURIComponent(search)}` : '/api/content/videos';
    return fetchAPI(endpoint);
  },

  async getVideo(id) {
    return fetchAPI(`/api/content/videos/${id}`);
  },

  async getLearningResources(search = '') {
    const endpoint = search ? `/api/content/learning-resources?search=${encodeURIComponent(search)}` : '/api/content/learning-resources';
    return fetchAPI(endpoint);
  },

  async getStats() {
    return fetchAPI('/api/content/stats');
  }
};

/**
 * Loading Screen API
 */
export const loadingScreenAPI = {
  async getPersonalizedContent() {
    return fetchAPI('/api/loading-screen');
  }
};

/**
 * Bookmarks API
 */
export const bookmarksAPI = {
  async getBookmarks(contentType = null) {
    const endpoint = contentType ? `/api/bookmarks?contentType=${contentType}` : '/api/bookmarks';
    return fetchAPI(endpoint);
  },

  async isBookmarked(contentType, contentId) {
    return fetchAPI(`/api/bookmarks/check/${contentType}/${contentId}`);
  },

  async toggleBookmark(contentType, contentId) {
    return fetchAPI('/api/bookmarks/toggle', {
      method: 'POST',
      body: JSON.stringify({ contentType, contentId })
    });
  },

  async getCounts() {
    return fetchAPI('/api/bookmarks/counts');
  }
};

/**
 * Push Notifications API
 */
export const pushAPI = {
  async getVapidPublicKey() {
    return fetchAPI('/api/push/vapid-public-key');
  },

  async subscribe(subscription) {
    return fetchAPI('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription })
    });
  },

  async unsubscribe(endpoint) {
    return fetchAPI('/api/push/unsubscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint })
    });
  },

  async getSubscriptions() {
    return fetchAPI('/api/push/subscriptions');
  }
};

/**
 * Progress API (Dashboard Stats)
 */
export const progressAPI = {
  async getStats() {
    return fetchAPI('/api/progress/stats');
  },

  async getBenchmarks() {
    return fetchAPI('/api/progress/benchmarks');
  },

  async getJourneyInsights() {
    return fetchAPI('/api/progress/journey-insights');
  }
};

/**
 * Coaches API - For user browsing and booking coaches
 */
export const coachesAPI = {
  async getAllCoaches() {
    return fetchAPI('/api/coaches');
  },

  async searchCoaches(query, limit = 3) {
    return fetchAPI('/api/coaches/search', {
      method: 'POST',
      body: JSON.stringify({ query, limit })
    });
  },

  async getCoach(coachId) {
    return fetchAPI(`/api/coaches/${coachId}`);
  },

  async getCoachSlots(coachId, from, to) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const queryString = params.toString();
    return fetchAPI(`/api/coaches/${coachId}/slots${queryString ? `?${queryString}` : ''}`);
  }
};

/**
 * Sessions API - For user session management
 */
export const sessionsAPI = {
  async bookSession(coachId, scheduledStart) {
    return fetchAPI('/api/sessions', {
      method: 'POST',
      body: JSON.stringify({ coachId, scheduledStart })
    });
  },

  async getUpcomingSessions() {
    return fetchAPI('/api/sessions/upcoming');
  },

  async getNextSession() {
    return fetchAPI('/api/sessions/next');
  },

  async getSessionHistory(limit = 20, offset = 0) {
    return fetchAPI(`/api/sessions/history?limit=${limit}&offset=${offset}`);
  },

  async getSession(sessionId) {
    return fetchAPI(`/api/sessions/${sessionId}`);
  },

  async getJoinStatus(sessionId) {
    return fetchAPI(`/api/sessions/${sessionId}/join`);
  },

  async cancelSession(sessionId, reason = null) {
    return fetchAPI(`/api/sessions/${sessionId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  },

  async rescheduleSession(sessionId, newStartTime) {
    return fetchAPI(`/api/sessions/${sessionId}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ newStartTime })
    });
  }
};

/**
 * Enterprise Dashboard API - For organization admins
 */
export const enterpriseDashboardAPI = {
  async getCompanies() {
    return fetchAPI('/api/enterprise/companies');
  },

  async getDashboard(organizationId) {
    return fetchAPI(`/api/enterprise/dashboard/${organizationId}`);
  },

  async getTagTrends(organizationId) {
    return fetchAPI(`/api/enterprise/tag-trends/${organizationId}`);
  },

  async syncCompanies() {
    return fetchAPI('/api/enterprise/sync', {
      method: 'POST'
    });
  }
};

/**
 * Coach Dashboard API - For coach-specific operations
 */
export const coachDashboardAPI = {
  // Profile
  async getMyProfile() {
    return fetchAPI('/api/coaches/me/profile');
  },

  // Availability
  async getMyAvailability() {
    return fetchAPI('/api/coaches/me/availability');
  },

  async addAvailability(dayOfWeek, startTime, endTime, timezone) {
    return fetchAPI('/api/coaches/me/availability', {
      method: 'POST',
      body: JSON.stringify({ dayOfWeek, startTime, endTime, timezone })
    });
  },

  async updateAvailability(availabilityId, updates) {
    return fetchAPI(`/api/coaches/me/availability/${availabilityId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  async deleteAvailability(availabilityId) {
    return fetchAPI(`/api/coaches/me/availability/${availabilityId}`, {
      method: 'DELETE'
    });
  },

  // Blocked slots
  async getBlockedSlots(from, to) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const queryString = params.toString();
    return fetchAPI(`/api/coaches/me/blocked-slots${queryString ? `?${queryString}` : ''}`);
  },

  async addBlockedSlot(blockedStart, blockedEnd, reason = null) {
    return fetchAPI('/api/coaches/me/blocked-slots', {
      method: 'POST',
      body: JSON.stringify({ blockedStart, blockedEnd, reason })
    });
  },

  async deleteBlockedSlot(blockedSlotId) {
    return fetchAPI(`/api/coaches/me/blocked-slots/${blockedSlotId}`, {
      method: 'DELETE'
    });
  },

  // Users
  async getMyUsers() {
    return fetchAPI('/api/coaches/me/users');
  },

  async getUserHistory(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/history`);
  },

  async getUserStats(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/stats`);
  },

  async getUserSummaries(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/summaries`);
  },

  async getUserNotes(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/notes`);
  },

  async createUserNote(userId, noteText) {
    return fetchAPI(`/api/coaches/me/users/${userId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ noteText })
    });
  },

  async updateUserNote(userId, noteId, noteText) {
    return fetchAPI(`/api/coaches/me/users/${userId}/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify({ noteText })
    });
  },

  async deleteUserNote(userId, noteId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/notes/${noteId}`, {
      method: 'DELETE'
    });
  },

  async getUserObservations(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/observations`);
  },

  async getUserTodos(userId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/todos`);
  },

  async getUserMessages(userId, options = {}) {
    const { limit = 20, page = 1, allConversations = false } = options;
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('page', page);
    if (allConversations) {
      params.append('allConversations', 'true');
    }
    return fetchAPI(`/api/coaches/me/users/${userId}/messages?${params.toString()}`);
  },

  async createUserTodo(userId, todoData) {
    return fetchAPI(`/api/coaches/me/users/${userId}/todos`, {
      method: 'POST',
      body: JSON.stringify(todoData)
    });
  },

  async updateUserTodo(userId, todoId, updates) {
    return fetchAPI(`/api/coaches/me/users/${userId}/todos/${todoId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  async deleteUserTodo(userId, todoId) {
    return fetchAPI(`/api/coaches/me/users/${userId}/todos/${todoId}`, {
      method: 'DELETE'
    });
  },

  // Stats
  async getMyStats() {
    return fetchAPI('/api/coaches/me/stats');
  },

  // Sessions (coach view)
  async getUpcomingSessions(limit = 20) {
    return fetchAPI(`/api/sessions/coach/upcoming?limit=${limit}`);
  },

  async getTodaySessions() {
    return fetchAPI('/api/sessions/coach/today');
  },

  async getPastSessions(limit = 20) {
    return fetchAPI(`/api/sessions/coach/past?limit=${limit}`);
  },

  async getCoachJoinStatus(sessionId) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/join`);
  },

  async cancelSession(sessionId, reason = null) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  },

  async rescheduleSession(sessionId, newStartTime) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ newStartTime })
    });
  },

  async completeSession(sessionId) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/complete`, {
      method: 'POST'
    });
  },

  async markNoShow(sessionId) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/no-show`, {
      method: 'POST'
    });
  },

  // Notes
  async getSessionNotes(sessionId) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/notes`);
  },

  async addSessionNote(sessionId, noteText) {
    return fetchAPI(`/api/sessions/coach/${sessionId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ noteText })
    });
  },

  async updateSessionNote(noteId, noteText) {
    return fetchAPI(`/api/sessions/coach/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify({ noteText })
    });
  },

  // Admin functions (full permission only)
  async getAllCoaches() {
    return fetchAPI('/api/coaches/admin/all');
  },

  async updateCoachStatus(coachId, isActive) {
    return fetchAPI(`/api/coaches/admin/${coachId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive })
    });
  },

  async getCoachAvailability(coachId) {
    return fetchAPI(`/api/coaches/admin/${coachId}/availability`);
  },

  async getSystemStats() {
    return fetchAPI('/api/coaches/admin/stats');
  },

  async regenerateEmbeddings() {
    return fetchAPI('/api/coaches/admin/embeddings/regenerate', {
      method: 'POST'
    });
  }
};

export { getToken, setToken, removeToken };
